import React from "react";
import {
    Card,
    Stack,
    Text,
    Group,
    Avatar,
    Skeleton,
    ActionIcon,
    Tooltip,
    Divider,
    Badge,
} from "@mantine/core";
import { IconStar, IconStarFilled } from "@tabler/icons-react";
import { useDispatch, useSelector } from "react-redux";
import CompetitionServiceInstance from "../../../services/CompetitionService";
import { isValidValue, setFormFieldValue } from "../../../helpers/FormHelper";
import { setFavouriteCompetitions } from "../../../store/slices/MemberSlice";
import { notification } from "antd";

const Context = React.createContext({ name: 'Default' });

export default function FavouriteLeagues() {
    const token = useSelector((state) => state.Auth.token);
    const favourites = useSelector((state) => state.Member.favouriteCompetitions);
    const rows = useSelector((state) => state.Utility.coverageCompetitions);
    const dispatch = useDispatch();

    const [api, contextHolder] = notification.useNotification();
    const openNotification = (placement) => {
        api.info({
            message: `Notification alert!`,
            description: <Context.Consumer>{({ name }) => `You should login to set leagues to favourite!`}</Context.Consumer>,
            placement,
        });
    };

    const toggle = async (competition, isFavourite) => {
        const isValidToken = isValidValue(token);
        if (isValidToken === false) {
            openNotification('bottomRight');
            return;
        }

        const competitionId = setFormFieldValue(competition?.id);
        if (isFavourite === true) {
            const response = await CompetitionServiceInstance.removeFavourite(token, competitionId);
            const { success } = response.data;
            if (success === true) {
                const updatedFavourites = favourites?.filter((item) => item?.id !== competitionId);
                const distinctFavourites = [...new Map(updatedFavourites.map(item => [item.id, item])).values()];
        
                dispatch(setFavouriteCompetitions(distinctFavourites));
            }
        }        

        if (isFavourite === false) {
            const response = await CompetitionServiceInstance.setFavourite(token, competitionId);
            const responseData = response.data;
            const { success, result } = responseData;
            if (success === true) {
                const newFavouriteCompetition = result?.competition;
                dispatch(setFavouriteCompetitions([...favourites, newFavouriteCompetition]));
            }
        }
    };

    const RowSkeleton = () => (
        <div
            style={{
                borderRadius: 12,
                padding: 8,
                border: "1px solid rgba(0,0,0,.06)",
                background: "linear-gradient(180deg,#fff,#fafafa)",
            }}
        >
            <Skeleton height={24} radius="sm" />
        </div>
    );

    return (
        <Card withBorder radius="lg" p="md" shadow="sm" style={{ overflow: "hidden" }}>
            {contextHolder}
            <Group justify="space-between" align="center" mb="xs">
                <div>
                    <Text fw={800} size="sm">
                        Competitions
                    </Text>
                    <Text c="dimmed" size="xs">
                        Pin the competition you care about to personalize fixtures & predictions
                    </Text>
                </div>
                <Badge variant="light" color="blue" radius="sm">
                    {favourites.length} selected
                </Badge>
            </Group>
            <Divider my="sm" />

            <Stack gap="xs">
                {!rows
                    ? Array.from({ length: 8 }).map((_, i) => <RowSkeleton key={i} />)
                    : rows.map((r, idx) => {
                        const isFav = isValidValue(favourites.find((item) => item?.competitionId === r?.competitionId));
                        return (
                            <Group
                                key={r.id}
                                justify="space-between"
                                wrap="nowrap"
                                style={{
                                    border: "1px solid rgba(0,0,0,.06)",
                                    borderRadius: 12,
                                    padding: 10,
                                    background: "#fff",
                                    transition: "box-shadow .18s ease, transform .18s ease, border-color .18s ease",
                                    boxShadow: "0 6px 18px rgba(0,0,0,.05)",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,.08)";
                                    e.currentTarget.style.borderColor = "rgba(0,0,0,.10)";
                                    e.currentTarget.style.transform = "translateY(-1px)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,.05)";
                                    e.currentTarget.style.borderColor = "rgba(0,0,0,.06)";
                                    e.currentTarget.style.transform = "translateY(0)";
                                }}
                            >
                                <Group gap="sm" wrap="nowrap">
                                    <Avatar src={r.logo} size={28} radius="sm" />
                                    <Text size="sm" fw={600} style={{ lineHeight: 1 }}>
                                        {r.name}
                                    </Text>
                                    {/* <Text size="xs" fw={400} style={{ lineHeight: 1 }}>
                                        {r.country}
                                    </Text> */}
                                </Group>

                                <Tooltip label={isFav ? "Unfavorite" : "Add to favorites"} withArrow>
                                    <ActionIcon
                                        onClick={() => toggle(r, isFav)}
                                        variant={isFav ? "filled" : "subtle"}
                                        color={isFav ? "yellow" : "gray"}
                                        radius="xl"
                                        size="md"
                                        aria-label={isFav ? "Unfavorite league" : "Favorite league"}
                                    >
                                        {isFav ? <IconStarFilled size={18} /> : <IconStar size={18} />}
                                    </ActionIcon>
                                </Tooltip>
                            </Group>
                        );
                    })}
            </Stack>
            <Text size="xs" c="dimmed" mt="sm">
                Your favorite comopetitions are used to prioritize fixtures, notifications, and model highlights.
            </Text>
        </Card>
    );
}
